<template>
  <div class="show-user">
    <Loading v-if="loading" />
    <div id="top"></div>
    <user-profile
      :user="user"
      :account="account"
      :accounts="accounts"
      :read_only="read_only"
      :busy="busy"
      :user_creation="user_creation"
      @read_only_changed="toggle_read_only()"
      @update_user="update_user($event.user)"
      @enable_user="enable_user()"
      @disable_user="disable_user()"
      @resend_verification="resend_verification()"
      @create_user="create_user($event.user)"
      :key="profile_key"
    ></user-profile>
    <confirm-modal
      ref="confirm"
      title="Disable?"
      confirm-btn-text="OK"
      cancel-btn-text="Cancel"
    >
      <div style="max-width: 350px">
        Are you sure you want to disable this user?
      </div>
    </confirm-modal>
    <confirm-modal
      ref="another_user"
      title="Create another user?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
    >
    </confirm-modal>
  </div>
</template>

<script>
import adminApi from "@/api/admin";
import UserProfile from "@/components/user/UserProfile.vue";
import ConfirmModal from "@/components/shared/ConfirmModal";
import Loading from "@/components/shared/Loading.vue";
import { mapGetters } from "vuex";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

export default {
  components: {
    UserProfile,
    ConfirmModal,
    Loading,
  },
  data() {
    return {
      user: null,
      account: null,
      accounts: [],
      loading: false,
      read_only: true,
      busy: false,
      user_creation: false,
      profile_key: 0,
    };
  },
  mounted() {
    this.gather_page_data();
  },
  computed: {
    ...mapGetters(["user_details"]),
  },
  setup() {
    const { SHOW_SNACKBAR, SHOW_DIALOG } = uiStore.useMutations([
      "SHOW_SNACKBAR",
      "SHOW_DIALOG",
    ]);
    return { SHOW_SNACKBAR, SHOW_DIALOG };
  },
  watch: {
    $route() {
      this.gather_page_data();
    },
  },
  methods: {
    get_account_info(account_id) {
      return adminApi
        .get_account(account_id)
        .then((response) => {
          this.account = response.data.account;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    toggle_read_only() {
      this.read_only = !this.read_only;
      if (this.read_only) {
        this.gather_page_data();
      }
    },

    async disable_user() {
      this.$refs.confirm.show().then((yes) => {
        if (yes) {
          this.busy = true;
          const account_id = this.$route.params.account_id;
          const user_id = this.$route.params.user_id;

          return adminApi
            .disable_user(account_id, user_id)
            .then(() => {
              this.read_only = true;
              this.gather_page_data();
              this.busy = false;
            })
            .catch(() => {
              this.busy = false;
            });
        }
      });
    },

    enable_user() {
      this.busy = true;

      const account_id = this.$route.params.account_id;
      const user_id = this.$route.params.user_id;
      return adminApi
        .patch_user(account_id, user_id, {})
        .then(() => {
          this.read_only = true;
          this.gather_page_data();
          this.busy = false;
        })
        .catch(() => {
          this.busy = false;
        });
    },

    update_user(user) {
      this.busy = true;
      const updated = {
        name: user.name,
        admin: user.admin,
        country_code: user.country_code,
        job_title: user.job_title,
        limits: {
          annual_subscription_fee_limit:
            user.limits.annual_subscription_fee_limit,
          fresh_imagery_fee_limit: user.limits.fresh_imagery_fee_limit,
          standard_imagery_fee_limit: user.limits.standard_imagery_fee_limit,
          training_imagery_fee_limit: user.limits.training_imagery_fee_limit,
          tasking_imagery_fee_limit: user.limits.tasking_imagery_fee_limit,
        },
      };

      if (updated.job_title == "") {
        updated["job_title"] = "";
      } else if (!updated.job_title) {
        delete updated["job_title"];
      }

      const account_id = this.$route.params.account_id;
      const user_id = this.$route.params.user_id;

      return adminApi
        .patch_user(account_id, user_id, updated)
        .then(() => {
          this.read_only = true;
          this.gather_page_data();
          this.busy = false;
        })
        .catch(() => {
          this.busy = false;
          this.loading = false;
        });
    },

    resend_verification() {
      this.busy = true;
      const account_id = this.$route.params.account_id;
      const user_id = this.$route.params.user_id;
      return adminApi
        .resend_verification(account_id, user_id)
        .then((response) => {
          this.busy = false;
          this.SHOW_SNACKBAR({
            text: `Sent verification email to ${response.data.user.email}`,
            color: "blue_darken_4",
          });
        })
        .catch(() => {
          this.busy = false;
        });
    },

    create_user(user) {
      this.loading = true;
      // hard copy user
      let new_user = JSON.parse(JSON.stringify(user));

      let account_id = "";

      // super admins and support users have access to many accounts
      if (this.accounts.length > 0) {
        account_id = new_user.account_id;
        // make sure account id is set
        if (!account_id) {
          this.SHOW_DIALOG({
            text: "Account is required",
            color: "error",
          });
          return;
        }
      }
      // normal admins can only create for their own account so we can assume here
      else {
        account_id = this.user_details.account_id;
      }

      // clean up values we don't need
      delete new_user["active"];
      delete new_user["account_id"];

      this.busy = true;

      adminApi
        .post_user(account_id, new_user)
        .then((response) => {
          this.busy = false;
          this.loading = false;
          this.$refs.another_user.show().then((yes) => {
            if (yes) {
              this.user = {
                limits: {},
              };
              // set account for super admins
              if (this.accounts.length > 0) {
                this.user.account_id = account_id;
              }
              this.profile_key += 1;
              // scroll to top
              const element = document.getElementById("top");
              element.scrollIntoView();
            } else {
              this.user_creation = false;
              this.read_only = true;
              this.$router.push({
                name: "user-details",
                params: {
                  account_id: account_id,
                  user_id: response.data.user.user_id,
                },
              });
            }
          });
        })
        .catch(() => {
          this.busy = false;
          this.loading = false;
        });
    },

    waitThenGetAccounts() {
      if (this.user_details.account_id) {
        //variable exists, stop waiting

        if (
          this.user_details.super_admin ||
          (this.user_details.support_accounts &&
            this.user_details.support_accounts.length > 0)
        ) {
          adminApi
            .fetch_all_accounts()
            .then((response) => {
              this.accounts = response;
              this.loading = false;
            })
            .catch(() => {
              this.loading = false;
            });
        } else {
          // user only has access to one account
          this.loading = false;
        }
      } else {
        setTimeout(this.waitThenGetAccounts, 200);
      }
    },

    gather_page_data() {
      this.loading = true;
      const account_id = this.$route.params.account_id;
      const user_id = this.$route.params.user_id;
      if (user_id) {
        return adminApi
          .get_user(account_id, user_id)
          .then((response) => {
            this.loading = false;
            this.user_creation = false;
            this.read_only = true;
            this.user = response.data.user;
            this.get_account_info(account_id);
          })
          .catch(() => {
            this.loading = false;
          });
      } else {
        this.waitThenGetAccounts();
        this.user = {
          limits: {},
        };
        this.user_creation = true;
        this.read_only = false;
        this.loading = false;
      }
    },
  },
};
</script>
